import type { NavigateFunction } from "react-router-dom";

import apiFetch from "@/app/auth/apiFetch";
import { apiErrorMessage } from "@/lib/billingGrant";

const API_URL = import.meta.env.VITE_API_URL;

// --------------------------------------------------------------------------- //
// Codes promo — backoffice /api/admin/promo-codes
// A code is a single-use voucher; redeemed by an organization, back turns it
// into an "Accès offert" (billing grant). The plaintext is returned ONCE, by the
// creation call; the list only carries its first group (`code_prefix`).
// --------------------------------------------------------------------------- //
export type PromoCodeStatus = "available" | "redeemed" | "expired" | "revoked";

export interface PromoCode {
  id: number;
  code_prefix: string;
  tier: string;
  seat_limit: number | null;
  grant_days: number | null;
  unlimited: boolean;
  expires_at: string | null;
  note: string;
  created_by: string;
  created_at: string;
  redeemed_at: string | null;
  redeemed_by_user_id: number | null;
  redeemed_by_email: string | null;
  redeemed_organization_id: number | null;
  redeemed_organization_name: string | null;
  billing_grant_id: number | null;
  revoked_at: string | null;
  revoked_by: string | null;
  status: PromoCodeStatus;
}

export interface PromoCodeCreateInput {
  tier: string;
  seat_limit: number | null;
  grant_days: number | null;
  unlimited: boolean;
  valid_days: number | null;
  note: string;
}

export interface PromoCodeCreated {
  promo_code: PromoCode;
  code: string;
}

async function readError(res: Response | undefined, fallback: string): Promise<Error> {
  const body = await res?.json?.().catch(() => null);
  return new Error(apiErrorMessage(body, fallback));
}

export async function listPromoCodes(
  navigate: NavigateFunction,
  params: { status?: PromoCodeStatus } = {}
): Promise<{ items: PromoCode[]; total: number }> {
  const qs = params.status ? `?status=${params.status}` : "";
  const res = await apiFetch(`${API_URL}/admin/promo-codes${qs}`, { method: "GET" }, navigate);
  if (!res?.ok) throw await readError(res, "Chargement des codes promo impossible");
  return res.json();
}

export async function createPromoCode(
  input: PromoCodeCreateInput,
  navigate: NavigateFunction
): Promise<PromoCodeCreated> {
  const res = await apiFetch(
    `${API_URL}/admin/promo-codes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
    navigate
  );
  if (!res?.ok) throw await readError(res, "Création du code promo impossible");
  return res.json();
}

export async function revokePromoCode(id: number, navigate: NavigateFunction): Promise<PromoCode> {
  const res = await apiFetch(`${API_URL}/admin/promo-codes/${id}`, { method: "DELETE" }, navigate);
  if (!res?.ok) throw await readError(res, "Révocation du code promo impossible");
  return res.json();
}
